<template>
  <div
    class="card-chatbot"
    :class="{
      'card-chatbot-active': active,
      'card-chatbot-inactive': !active,
    }"
  >
    <a-skeleton v-if="loading" :loading="loading" active :paragraph="{ rows: 2 }"> </a-skeleton>
    <template v-else>
      <a-row
        type="flex"
        justify="space-between"
        style="margin-bottom: 4px; display: flex; align-items: center"
      >
        <!--Canales-->
        <div class="card__channels display-flex">
          <a-space :size="8" v-if="chatbotData.channels_customers.length !== 0">
            <a-popover
              v-for="group in groupBy(
                chatbotData.channels_customers,
                (channel) => channel.channel.icon
              )"
              :key="group.icon"
              :title="group.title"
            >
              <template #content>
                <ul>
                  <li v-for="channel in group.items" :key="channel.id">
                    {{ channel.alias || channel.associate }}
                    <template v-if="channel.channel.id === ChannelType.Whatsapp">
                      ({{ channel.associate }})
                    </template>
                  </li>
                </ul>
              </template>

              <a-button type="link" class="button">
                <img
                  class="channel-icon"
                  :class="{ 'inactive-icon': chatbotData.status === ChatbotStatus.Inactive }"
                  :src="group.icon"
                  :alt="group.name"
                />
              </a-button>
            </a-popover>
          </a-space>

          <p v-else>No tiene canales</p>
        </div>
        <!--Acciones-->
        <div>
          <header>
            <a-popover trigger="click" v-model:visible="popoverVisible">
              <template #content>
                <div class="actions" @click="popoverVisible = false">
                  <a-button type="text" @click="handleEdit(chatbotData.id)">
                    <EditOutlined /> Editar chatbot
                  </a-button>

                  <a-button
                    type="text"
                    @click="handleShowConfirmDuplicate(chatbotData.id, chatbotData.name)"
                  >
                    <CopyOutlined /> Duplicar tu chatbot</a-button
                  >

                  <a-button
                    type="text"
                    @click="handleShowModalDownload(chatbotData.id, chatbotData.name)"
                  >
                    <DownloadOutlined /> Descargar reporte
                  </a-button>

                  <a-button
                    type="text"
                    danger
                    @click="!active && handleDelete(chatbotData.id, chatbotData.name)"
                  >
                    <DeleteOutlined /> Elimina tu chatbot
                  </a-button>
                </div>
              </template>

              <MoreOutlined style="font-size: 20px" />
            </a-popover>
          </header>
        </div>
      </a-row>

      <div class="details">
        <!-- Nombre de chatbot -->
        <h4 class="heading-h7 name ellipsis">
          {{ chatbotData.name }}
        </h4>
        <!-- Descripción -->
        <p class="body-1 description ellipsis">
          {{ chatbotData.description }}
        </p>
        <a-row>
          <a-button
            type="link"
            class="button schedule-details"
            @click="scheduleDetailsVisible = true"
          >
            Ver detalles de horario
          </a-button>
        </a-row>
      </div>

      <a-row type="flex" justify="space-between" class="row-footer">
        <div>
          <!--Estado de chatbot-->
          <a-tag :color="StatusProps[chatbotData.status].color">
            {{ StatusProps[chatbotData.status].text }}
          </a-tag>

          <a-tooltip v-if="tooltipWarning">
            <template #title>
              Actualmente, este chatbot no está atendiendo por el canal de WhatsApp porque no
              cuentas con saldo disponible.
            </template>
            <a-tag color="red"> Sin saldo </a-tag>
          </a-tooltip>
          <!--Si el chatbot es una copia-->
          <a-tag v-if="isCopy" color="orange"> Duplicado </a-tag>

          <a-tag v-if="chatbotData?.deleted_variables" color="warning">
            <a-tooltip
              title="Este chatbot estaba utilizando una variable que ha sido eliminada. Podrás activarlo de nuevo si seleccionas una nueva variable."
              placement="topLeft"
              arrowPointAtCenter
            >
              <WarningFilled />
            </a-tooltip>
            Variable eliminada
          </a-tag>

          <!-- If no has queue -->
          <a-tag v-else-if="isTransferWithoutQueue" color="warning">
            <a-tooltip
              title="Este chatbot estaba utilizando una cola que ha sido eliminada. Podrás activarlo de nuevo si seleccionas una nueva cola de transferencia."
              placement="topLeft"
              arrowPointAtCenter
            >
              <WarningFilled />
            </a-tooltip>
            Cola Eliminada
          </a-tag>

          <a-tag v-else-if="chatbotData?.has_update_api_connector" color="warning">
            <a-tooltip
              title="Se han detectado cambios en los conectores API de este chatbot. Revisa la configuración antes de activarlo."
              placement="topLeft"
              arrowPointAtCenter
            >
              <WarningFilled />
            </a-tooltip>
            Cambios detectados
          </a-tag>          
          
        </div>

        <!--Switch de estado-->
        <DisableButton
          :chatbotData="chatbotData"
          :disabled="!active && isTransferWithoutQueue"
          @update="handleUpdate"
        />
      </a-row>
    </template>
  </div>

  <PreviewNodesModal
    v-if="nodesPreviewVisible"
    :visible="nodesPreviewVisible"
    :chatbot="chatbotData"
    @close="nodesPreviewVisible = false"
  />

  <ScheduleDetailsModal
    v-if="scheduleDetailsVisible"
    :visible="scheduleDetailsVisible"
    :chatbot="chatbotData"
    @close="scheduleDetailsVisible = false"
  />

  <ModalDownload
    :visible="modalDownloadReport.visible"
    :title="modalDownloadReport.title"
    :id="chatbotData?.id"
    @onClose="handleCloseModalDownload"
  />
</template>

<script>
import { defineComponent, ref, computed, reactive } from "vue";
import { useRouter } from "vue-router";
import { Modal, message } from "ant-design-vue";
import {
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  WarningOutlined,
  DownloadOutlined,
  MoreOutlined,
  WarningFilled,
} from "@ant-design/icons-vue";

import {
  // changeChatbotStatus,
  // fetchChatbotDetails,
  duplicateChatbot,
  deleteChatbot,
} from "@/app/chatbots/services";
import { groupBy } from "@/app/ticketsManager/utils/groupBy";
import { ChatbotStatus, StatusProps } from "@/app/chatbots/utils/enums";
import { ChannelType } from "@/app/shared/utils/enums";

import ScheduleDetailsModal from "../modals/ScheduleDetailsModal.vue";
import PreviewNodesModal from "../modals/PreviewNodesModal.vue";
import DisableButton from "../buttons/DisableButton.vue";
import ModalDownload from "@/app/chatbots/components/modals/ModalDownload.vue";

export default defineComponent({
  name: "CardChatbot",
  components: {
    EditOutlined,
    CopyOutlined,
    DeleteOutlined,
    ScheduleDetailsModal,
    PreviewNodesModal,
    DisableButton,
    WarningOutlined,
    DownloadOutlined,
    ModalDownload,
    MoreOutlined,
    WarningFilled,
  },
  props: {
    chatbotData: {
      type: Object,
      required: false,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    isCopy: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const router = useRouter();
    const currentSelectedTime = ref(null);
    const loadingSwitch = ref(false);

    const nodesPreviewVisible = ref(false);
    const scheduleDetailsVisible = ref(false);
    const disabled = ref(false);
    const popoverVisible = ref(false);

    const actionModal = reactive({
      visible: false,
      total_tickets: null,
    });
    const modalDownloadReport = reactive({
      title: "",
      visible: false,
    });

    const active = computed(() => props.chatbotData.status === ChatbotStatus.Active);
    const isTransferWithoutQueue = computed(() => props.chatbotData.deleted_queues);
    const tooltipWarning = computed(() => {
      return false;
    });

    const handleEdit = (id) => {
      router.push(`/chatbots/tree/${id}`);
    };

    // const handleChangeStatus = async (id) => {
    //   loadingSwitch.value = true;

    //   const chatbotDetails = await fetchChatbotDetails(props.chatbotData.id);

    //   if (active.value && chatbotDetails.total_tickets) {
    //     actionModal.visible = true;
    //     actionModal.total_tickets = chatbotDetails.total_tickets;
    //   } else {
    //     try {
    //       const body = { status: active.value ? ChatbotStatus.Inactive : ChatbotStatus.Active };
    //       await changeChatbotStatus(id, body);
    //       handleUpdate();
    //     } catch (error) {
    //       const errorMessage = error.data?.Status || "Error al activar el chatbot";

    //       message.error(errorMessage);
    //       console.log(error);
    //     }
    //   }

    //   loadingSwitch.value = false;
    // };

    const handleShowConfirmDuplicate = (chatbotId, chatbotName) => {
      Modal.confirm({
        title: `¿Estás segur@ de duplicar el chatbot "${chatbotName}"?`,
        content:
          "Recuerda que se duplicará toda la configuración y el flujo de este chatbot, se recomienda revisar antes de activarlo",
        okText: "Duplicar",
        onOk: async () => {
          try {
            await duplicateChatbot({ bot: chatbotId });
            message.success("Se duplicó exitosamente");
            handleUpdate();
          } catch (error) {
            const errorMessage = error?.getErrorMessage() || "Error al duplicar";
            message.error(errorMessage);
            console.log(error);
          }
        },
        centered: true,
        cancelText: "Cancelar",
        width: 436,
      });
    };

    const handleDelete = (chatbotId, chatbotName) => {
      Modal.confirm({
        title: `¿Estás seguro de eliminar el chatbot "${chatbotName}"?`,
        content: "Recuerda que ya no podrás recuperarlo más adelante.",
        okText: "Eliminar",
        onOk: async () => {
          try {
            await deleteChatbot(chatbotId);
            message.success("Se eliminó exitosamente");
            handleUpdate();
          } catch (error) {
            const errorMessage = error?.getErrorMessage() || "Error al eliminar";

            message.error(errorMessage);
            console.log(error);
          }
        },
        centered: true,
        cancelText: "Cancelar",
        width: 436,
      });
    };

    const handleUpdate = () => emit("update");

    const handleShowModalDownload = (chatbotId, chatbotName) => {
      console.log("handleShowModalDownload", chatbotId, chatbotName);
      modalDownloadReport.title = `Descargar reporte - "${chatbotName}"`;
      modalDownloadReport.visible = true;
    };

    const handleCloseModalDownload = () => {
      modalDownloadReport.title = "";
      modalDownloadReport.visible = false;
    };

    return {
      ChatbotStatus,
      StatusProps,
      active,
      tooltipWarning,
      currentSelectedTime,
      nodesPreviewVisible,
      scheduleDetailsVisible,
      loadingSwitch,
      disabled,
      actionModal,
      groupBy,
      modalDownloadReport,
      // handleChangeStatus,
      isTransferWithoutQueue,
      handleEdit,
      handleShowConfirmDuplicate,
      handleDelete,
      handleUpdate,
      ChannelType,
      handleShowModalDownload,
      handleCloseModalDownload,
      popoverVisible,
    };
  },
});
</script>

<style lang="sass" scoped>
p
  margin-bottom: 0
.card-chatbot
  background-color: #FFFFFF
  padding: 12px 12px 12px
  border-radius: 8px
  text-align: left
  width: 100%
  border: 1px solid $gray-5
  height: 210px
  position: relative
  // &:hover
  //   box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15)
  //   border-radius: 8px
  .name
    margin: 8px 0 0 0
    font-weight: 600
    font-size: 14px

  .description
    margin-bottom: 4px
    line-height: 1rem
    -webkit-line-clamp: 2 !important
  .row
    &__col_flex
      display: flex
      align-items: center
      p
        color: $gray-7
        margin-left: 8px
    ul, li
      margin: 0
      padding: 0
      list-style: none
    ul
      margin: 4px 0
      li
        display: flex
        align-items: center
        span
          margin: 0 0 0 4px
  .row-footer
    border-top: 1px solid $gray-5
    padding: 8px 0 0 0
    position: absolute
    width: -webkit-fill-available
    bottom: 0
    left: 0
    margin: 12px
.card-chatbot-active
  .name
    color: $gray-9
  .description
    color: $gray-7
.card-chatbot-inactive
  .name
    color: $gray-7
  .description, ul
    color: $gray-7
  .row
    &__col_flex
      p
        color: $gray-3

.actions
  display: flex
  flex-direction: column
  gap: 8px
  > button
    text-align: left
    width: 100%

.button
  padding: 0

.details
  height: calc(100% - 64px - 8px)
  position: relative

.schedule-details
  position: absolute
  bottom: 0
  left: 0

.card__actions-icon
  &--delete
    color: $red-6
.card__channel
  margin-right: 8px

.channel-icon
  width: 16px
  height: 16px

.inactive-icon
  filter: grayscale(100%)
</style>

<style lang="sass">
svg.icon-inactive-path
  path
    fill: $gray-4
</style>
